"use client";

import { useEffect, useRef } from "react";
import type { ProductDetailGtmData } from "@/ui/pages/product-detail-page/product-detail-page.adapter";
import { type EcommerceItem, viewItemToDataLayer } from "@/utils/gtm.utils";

/** Maps the PDP view model's GA4 data onto the shared ecommerce item shape. */
export function gtmDataToEcommerceItem(
  gtm: ProductDetailGtmData,
  overrides?: Partial<EcommerceItem>
): EcommerceItem {
  return {
    item_id: gtm.itemId,
    item_name: gtm.itemName,
    item_variant_id: gtm.itemVariantId,
    price: gtm.price,
    currency: gtm.currency,
    item_brand: gtm.itemBrand,
    item_variant: gtm.itemVariant,
    item_stock: gtm.itemStock,
    ...(gtm.discount ? { discount: gtm.discount } : {}),
    ...(gtm.coupon ? { coupon: gtm.coupon } : {}),
    ...overrides,
  };
}

/**
 * Pushes `view_item` once per PDP load, using the on-load article. Keyed by the
 * base product so re-renders and in-page colour/size switches never duplicate
 * it, while navigating to a different product (even without a remount) fires a
 * fresh event. `view_item` carries no quantity — one unit is implied.
 */
export function useGtmProductViewTracking(
  gtm: ProductDetailGtmData | undefined
): void {
  const firedItemIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!gtm?.itemId || firedItemIdRef.current === gtm.itemId) {
      return;
    }

    firedItemIdRef.current = gtm.itemId;
    viewItemToDataLayer(gtmDataToEcommerceItem(gtm));
  }, [gtm]);
}
